import { Suspense } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sidebar } from '@components/Sidebar/Sidebar';
import { TopBar } from '@components/TopBar/TopBar';
import { TopBarSchedulePickerProvider } from '@components/TopBar/topBarSchedulePicker';
import { ComingSoon } from '@components/Placeholder';
import { PageFrame } from '@components/ui/PageFrame/PageFrame';
import { routes } from '@src/routes';
import { AuthProvider } from '@contexts/AuthContext';
import { LoginPage } from '@components/Pages/LoginPage/LoginPage';
import { SetPasswordPage } from '@components/Pages/SetPasswordPage/SetPasswordPage';
import { ConnectProviderPage } from '@components/Pages/ConnectProviderPage/ConnectProviderPage';
import { ProtectedRoute } from '@components/ProtectedRoute/ProtectedRoute';
import { RouteErrorBoundary } from '@components/RouteErrorBoundary/RouteErrorBoundary';
import { useEnsureWorkingSite } from '@hooks/useEnsureWorkingSite';
import { APP_CONTENT_PANE_PROPS } from '@hooks/useAppContentPane';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './App.module.css';

function Shell() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const firstSegment = pathname === '/' ? 'portfolio' : pathname.split('/')[1];
  const titleKey = `pages.${firstSegment}`;
  const pageTitle = t(titleKey, { defaultValue: t('pages.portfolio') });
  useEnsureWorkingSite();

  return (
    <div className={styles.shell}>
      <Sidebar />
      {/* The TopBar breadcrumb hosts the active page's schedule picker, so the
          provider wraps both the bar and the routed pages that publish it. */}
      <TopBarSchedulePickerProvider>
        <TopBar pageTitle={pageTitle} />
        <div className={styles.content}>
          {/* The pane scrolls with the app's overlay scrollbar; the marker sits
              on its viewport — the element that actually scrolls. */}
          <ScrollArea className={styles.pane} viewportProps={APP_CONTENT_PANE_PROPS}>
            <main className={styles.main}>
              {/* A page that throws shows a Reload fallback in place, inside the
                  shell, instead of unmounting the whole app; navigating clears it. */}
              <RouteErrorBoundary resetKey={pathname}>
                <Routes>
                  {routes.map((r) => (
                    <Route key={r.path} path={r.path} element={<PageFrame layout={r.layout ?? {}}>{r.element}</PageFrame>} />
                  ))}
                  <Route
                    path="*"
                    element={
                      <PageFrame layout={{}}>
                        <ComingSoon label="Page not found" phase="—" />
                      </PageFrame>
                    }
                  />
                </Routes>
              </RouteErrorBoundary>
            </main>
          </ScrollArea>
        </div>
      </TopBarSchedulePickerProvider>
    </div>
  );
}

function RootRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public — reached from the invite / reset email link. No auth guard:
          the user is setting their password precisely because they can't log
          in yet. The token in the URL is the credential. */}
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route
        path="/connect/:provider"
        element={
          <ProtectedRoute>
            <ConnectProviderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Shell />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <Suspense fallback={null}>
      <BrowserRouter>
        <AuthProvider>
          <RootRoutes />
        </AuthProvider>
      </BrowserRouter>
    </Suspense>
  );
}
